// src/pages/SummaryIndomaretRegistration.jsx
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  RadioGroup,
  Radio,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import api from '../../services/apiClient';
import RegStatusDialog from '../RegCompletePopup/RegStatusDialog';

const RADIUS_PX = 20;
const SECTION_BORDER = '#E5E7EB';

const DROP_BAG_TOC_TEXT = `<ul>
  <li>No valuables (organizer is not responsible for loss)</li>
  <li>Operating hours: Race Day only</li>
  <li>Claim with bib/wristband &amp; keep your receipt</li>
</ul>`;

const HOME_PATH =
  '/event/ZXlKcFpDSTZPRGtzSW5SNWNHVWlPaUpQUmtaTVNVNUZJbjA9K2lyYWNlMjAyMA==';

const TERMS_TOC_TEXT = `<article class="terms-and-conditions">
  <header>
    <h1>
      Syarat &amp; Ketentuan Umum – Indomaret Run 2026
    </h1>

    <h2>
      Terms &amp; Conditions – Indomaret Run 2026
    </h2>
  </header>

  <section>
    <h3>
      1. Pemberitahuan Penting / Important Notices
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Acara ini bernama
          <strong>Indomaret Run 2026</strong>
          (“Acara”), dengan
          <strong>Indomaret</strong>
          sebagai Penyelenggara dan
          <strong>IRACE</strong>
          sebagai Manajemen Lomba.
        </li>

        <li>
          Peserta (“IDM Runners”) wajib membaca dan memahami
          Syarat &amp; Ketentuan (“S&amp;K”) ini sebelum
          melakukan pendaftaran.
        </li>

        <li>
          Dengan mendaftar, peserta dianggap telah membaca,
          memahami, dan menyetujui seluruh S&amp;K serta
          peraturan terkait.
        </li>

        <li>
          Penyelenggara berhak meminta hasil pemeriksaan medis
          apabila dianggap perlu.
        </li>

        <li>
          Peserta menyatakan berada dalam kondisi sehat dan
          bertanggung jawab penuh atas kesehatannya selama
          Acara berlangsung.
        </li>

        <li>
          Biaya pendaftaran yang telah dibayarkan
          <strong>tidak dapat dikembalikan</strong>.
        </li>

        <li>
          Penyelenggara berhak menolak atau membatalkan
          keikutsertaan apabila ditemukan data palsu atau
          peserta tidak memenuhi persyaratan.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          This event is called
          <strong>Indomaret Run 2026</strong>
          (“Event”), organized by
          <strong>Indomaret</strong>,
          with race management by
          <strong>IRACE</strong>.
        </li>

        <li>
          Participants (“IDM Runners”) must read and understand
          these Terms &amp; Conditions (“T&amp;C”) before
          registering.
        </li>

        <li>
          By registering, participants are deemed to have read,
          understood, and accepted all T&amp;C and related rules.
        </li>

        <li>
          The Organizer reserves the right to request medical
          examination results if necessary.
        </li>

        <li>
          Participants declare that they are in good health and
          fully responsible for their own condition during
          the Event.
        </li>

        <li>
          Registration fees are
          <strong>non-refundable</strong>.
        </li>

        <li>
          The Organizer reserves the right to reject or cancel
          participation if false data is provided or applicable
          requirements are not met.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      2. Pendaftaran / Registration
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Pendaftaran dilakukan melalui situs resmi atau
          platform pendaftaran yang ditunjuk.
        </li>

        <li>
          Konfirmasi pendaftaran akan dikirim melalui email
          setelah pembayaran diterima.
        </li>

        <li>
          Pendaftaran baru dinyatakan sah apabila biaya
          pendaftaran telah dibayar penuh.
        </li>

        <li>
          Prinsip
          <strong>“You Register, You Run”</strong>
          berlaku. Hanya peserta terdaftar yang diperbolehkan
          mengikuti lomba.
        </li>

        <li>
          Penyelenggara berhak membatalkan keikutsertaan atau
          hasil lomba apabila terjadi pelanggaran.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          Registration must be completed through the official
          website or designated registration platforms.
        </li>

        <li>
          Registration confirmation will be sent to the
          participant’s email once payment has been received.
        </li>

        <li>
          Registration is valid only upon full payment of
          the entry fee.
        </li>

        <li>
          The principle
          <strong>“You Register, You Run”</strong>
          applies. Only registered participants may join
          the race.
        </li>

        <li>
          The Organizer reserves the right to cancel
          participation or race results in the event of
          a violation.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      3. Persyaratan Peserta / Participant Requirements
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Pendaftaran dapat ditutup lebih awal apabila kuota
          peserta telah terpenuhi.
        </li>

        <li>
          Peserta wajib memastikan bahwa data yang diberikan
          akurat dan benar.
        </li>

        <li>
          Penyelenggara berhak menolak peserta yang pernah
          melakukan pelanggaran pada lomba sebelumnya.
        </li>

        <li>
          Acara dapat dibatalkan karena keadaan kahar
          (<em>force majeure</em>), termasuk bencana,
          cuaca ekstrem, wabah, atau keadaan lainnya
          di luar kendali Penyelenggara.
          Tidak ada pengembalian dana.
        </li>

        <li>
          Peserta bertanggung jawab atas kondisi kesehatannya
          sendiri. Penyelenggara tidak bertanggung jawab atas
          cedera atau kematian, kecuali apabila terbukti terjadi
          kelalaian berat.
        </li>

        <li>
          Peserta yang melanggar peraturan World Athletics
          atau peraturan lomba dapat didiskualifikasi.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          Registration may close early once the participant
          quota has been reached.
        </li>

        <li>
          Participants must ensure that all data provided
          is accurate and correct.
        </li>

        <li>
          The Organizer may reject participants who have
          committed violations in previous races.
        </li>

        <li>
          The Event may be cancelled due to force majeure,
          including disasters, extreme weather, epidemics,
          or other circumstances beyond the Organizer’s control.
          No refunds will be provided.
        </li>

        <li>
          Participants are responsible for their own health.
          The Organizer is not liable for injury or death,
          except in cases of proven gross negligence.
        </li>

        <li>
          Participants who violate World Athletics rules
          or race regulations may be disqualified.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      4. Pembatasan Peserta / Restrictions
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Usia minimum peserta sesuai kategori pada
          tanggal 22 November 2026 adalah:
          <ul>
            <li>
              <strong>5K:</strong>
              10 tahun.
            </li>

            <li>
              <strong>10K:</strong>
              14 tahun.
            </li>

            <li>
              <strong>21K:</strong>
              16 tahun.
            </li>
          </ul>
        </li>

        <li>
          Peserta yang sedang hamil, memiliki kondisi medis
          berisiko tinggi, atau tidak direkomendasikan oleh
          dokter
          <strong>tidak diperbolehkan mengikuti lomba</strong>.
        </li>

        <li>
          Peserta dilarang mengikuti lomba dalam pengaruh
          alkohol, obat-obatan terlarang, atau zat terlarang.
        </li>

        <li>
          Peserta dilarang menggunakan sepeda, sepatu roda,
          membawa hewan, atau menggunakan alat bantu lainnya,
          kecuali telah mendapatkan persetujuan dari
          Penyelenggara.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          The minimum participant age for each category
          as of 22 November 2026 is:
          <ul>
            <li>
              <strong>5K:</strong>
              10 years old.
            </li>

            <li>
              <strong>10K:</strong>
              14 years old.
            </li>

            <li>
              <strong>21K:</strong>
              16 years old.
            </li>
          </ul>
        </li>

        <li>
          Pregnant participants, participants with high-risk
          medical conditions, or participants without medical
          clearance are
          <strong>not permitted to participate</strong>.
        </li>

        <li>
          Participants must not race under the influence
          of alcohol, illegal drugs, or prohibited substances.
        </li>

        <li>
          The use of bicycles, roller skates, animals,
          or other aids not approved by the Organizer
          is strictly prohibited.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      5. Rute, Waktu &amp; Diskualifikasi /
      Route, Timing &amp; Disqualification
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Penyelenggara berhak menentukan atau mengubah
          rute lomba dengan pemberitahuan sebelumnya.
        </li>

        <li>
          Peserta wajib mengikuti instruksi marshal.
          Penyimpangan dari rute atau perilaku tidak sportif
          dapat mengakibatkan diskualifikasi.
        </li>

        <li>
          Peserta dilarang mengikuti lomba tanpa BIB resmi
          atau chip pencatat waktu.
        </li>

        <li>
          Batas waktu
          (<em>cut-off time</em>)
          berlaku sesuai kategori. Peserta yang melewati
          batas waktu tidak berhak menerima medali atau
          sertifikat.
        </li>

        <li>
          Keberatan hanya dapat diajukan dalam jangka waktu
          yang ditentukan setelah hasil lomba diumumkan.
        </li>

        <li>
          Penyelenggara berhak melakukan tes doping.
          Hasil tes positif akan mengakibatkan diskualifikasi.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          The Organizer may determine or adjust the race route
          with prior notice.
        </li>

        <li>
          Participants must follow marshal instructions.
          Route deviation or unsporting conduct may result
          in disqualification.
        </li>

        <li>
          Participating without an official BIB or timing chip
          is prohibited.
        </li>

        <li>
          Cut-off times apply according to each category.
          Participants who exceed the applicable time limit
          are not entitled to receive medals or certificates.
        </li>

        <li>
          Objections may only be submitted within the specified
          period after the results have been published.
        </li>

        <li>
          The Organizer may conduct doping tests.
          A positive test result will lead to disqualification.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      6. Hadiah &amp; Podium / Prizes &amp; Podium
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Pemenang ditentukan berdasarkan
          <em>gun time</em>
          atau sesuai dengan peraturan lomba yang berlaku.
        </li>

        <li>
          Setiap peserta hanya berhak memenangkan satu
          kategori podium.
        </li>

        <li>
          Pajak hadiah akan ditanggung oleh Indomaret.
        </li>

        <li>
          Status pemenang dinyatakan final setelah proses
          verifikasi data dan identitas selesai dilakukan.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          Winners are determined based on gun time
          or as otherwise specified in the applicable
          race regulations.
        </li>

        <li>
          Each participant is entitled to only one
          podium category.
        </li>

        <li>
          Prize taxes will be borne by Indomaret.
        </li>

        <li>
          Winner status becomes final only after data
          and identity verification has been completed.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      7. Penitipan Tas / Bag Deposit
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Fasilitas penitipan tas akan disediakan.
          Barang berharga seperti uang, telepon seluler,
          laptop, dan dokumen penting dilarang dititipkan.
        </li>

        <li>
          Penyelenggara tidak bertanggung jawab atas
          kehilangan atau kerusakan barang yang dititipkan.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          A bag deposit service will be provided.
          Valuables such as cash, mobile phones, laptops,
          and important documents must not be deposited.
        </li>

        <li>
          The Organizer is not liable for any loss
          or damage to deposited items.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      8. Dokumentasi / Photography &amp; Media
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Dengan mengikuti Acara, peserta memberikan izin
          kepada Penyelenggara dan mitra terkait untuk
          menggunakan foto atau video Acara untuk keperluan
          dokumentasi, publikasi, dan promosi tanpa
          kompensasi tambahan.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          By participating in the Event, participants grant
          the Organizer and its partners the right to use
          Event photos or videos for documentation,
          publication, and promotional purposes without
          additional compensation.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      9. Perubahan &amp; Keberatan /
      Amendments &amp; Objections
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Penyelenggara berhak mengubah, menambah,
          atau meniadakan ketentuan yang berlaku.
        </li>

        <li>
          Peserta dianggap telah menyetujui setiap perubahan
          setelah perubahan tersebut diumumkan melalui kanal
          resmi Acara.
        </li>

        <li>
          Keputusan Penyelenggara bersifat final dan
          tidak dapat diganggu gugat.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          The Organizer reserves the right to amend,
          add, or waive any applicable rules.
        </li>

        <li>
          Participants are deemed to have accepted
          any changes once those changes have been
          officially announced.
        </li>

        <li>
          Decisions made by the Organizer are final
          and binding.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      10. Pembebasan Tanggung Jawab /
      Disclaimer &amp; Limitation of Liability
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Peserta membebaskan Indomaret, IRACE, sponsor,
          dan pihak terkait lainnya dari segala klaim,
          kerugian, atau tuntutan yang timbul akibat
          keikutsertaan dalam Acara.
        </li>

        <li>
          Penyelenggara tidak bertanggung jawab atas
          kerugian tidak langsung, termasuk kehilangan
          keuntungan, reputasi, atau data.
        </li>

        <li>
          Apabila Penyelenggara terbukti bertanggung jawab,
          nilai kompensasi maksimum tidak akan melebihi
          biaya pendaftaran yang telah dibayarkan oleh
          peserta.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          Participants release Indomaret, IRACE, sponsors,
          and other related parties from all claims,
          losses, or demands arising from participation
          in the Event.
        </li>

        <li>
          The Organizer is not responsible for indirect
          losses, including loss of profit, reputation,
          or data.
        </li>

        <li>
          If the Organizer is found liable, the maximum
          compensation shall not exceed the registration
          fee paid by the participant.
        </li>
      </ul>
    </div>
  </section>
</article>`; // (keep your long HTML text as-is)
const WAIVER_TOC_TEXT = `<article class="waiver-release">
  <header>
    <h1>
      Surat Pernyataan &amp; Pelepasan Tanggung Jawab
    </h1>

    <h2>
      Waiver &amp; Release of Liability
    </h2>
  </header>

  <section>
    <h3>
      Pernyataan Peserta / Participant Declaration
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <p>
        Dengan ini saya menyatakan bahwa:
      </p>

      <ol>
        <li>
          Saya telah membaca, memahami, dan menyetujui seluruh
          <strong>Syarat &amp; Ketentuan</strong>
          serta
          <strong>Kebijakan Privasi</strong>
          Indomaret Run 2026.
        </li>

        <li>
          Saya menyatakan dalam kondisi sehat, cukup berlatih,
          dan secara medis layak mengikuti lomba lari.
        </li>

        <li>
          Saya berpartisipasi atas risiko pribadi saya sendiri.
          Penyelenggara
          (<strong>Indomaret</strong>)
          dan Manajemen Lomba
          (<strong>IRACE</strong>)
          tidak bertanggung jawab atas cedera, kecelakaan,
          kehilangan, atau kerusakan yang mungkin terjadi
          selama Acara, kecuali dalam hal kelalaian yang terbukti.
        </li>

        <li>
          Saya menyetujui bahwa Penyelenggara berhak mengambil
          tindakan medis darurat apabila diperlukan dan saya
          bertanggung jawab atas biaya medis tambahan yang
          mungkin timbul.
        </li>

        <li>
          Saya memahami bahwa biaya pendaftaran tidak dapat
          dikembalikan atau dipindahkan dengan alasan apa pun.
        </li>

        <li>
          Saya menyetujui penggunaan nama, foto, video,
          dan hasil lomba saya untuk keperluan dokumentasi,
          publikasi, dan promosi Acara tanpa kompensasi tambahan.
        </li>

        <li>
          Saya menyadari adanya risiko terkait cuaca,
          kondisi jalan, atau faktor lainnya yang berada
          di luar kendali Penyelenggara.
        </li>
      </ol>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <p>
        I hereby declare that:
      </p>

      <ol>
        <li>
          I have read, understood, and agreed to all
          <strong>Terms &amp; Conditions</strong>
          and the
          <strong>Privacy Policy</strong>
          of Indomaret Run 2026.
        </li>

        <li>
          I confirm that I am in good health,
          adequately trained, and medically fit
          to participate in the race.
        </li>

        <li>
          I participate at my own risk. The Organizer
          (<strong>Indomaret</strong>)
          and Race Management
          (<strong>IRACE</strong>)
          shall not be liable for any injury, accident,
          loss, or damage occurring during the Event,
          except in cases of proven negligence.
        </li>

        <li>
          I agree that the Organizer may provide
          emergency medical treatment if required,
          and I am responsible for any additional
          medical costs incurred.
        </li>

        <li>
          I understand that registration fees are
          non-refundable and non-transferable
          under any circumstances.
        </li>

        <li>
          I consent to the use of my name, photos,
          videos, and race results for documentation,
          publication, and promotional purposes
          without additional compensation.
        </li>

        <li>
          I acknowledge the risks associated with weather,
          road conditions, or other factors beyond
          the control of the Organizer.
        </li>
      </ol>
    </div>
  </section>
</article>`; // (keep your long HTML text as-is)
const PRIVACY_TOC_TEXT = `<article class="privacy-policy">
  <header>
    <h1>Kebijakan Privasi – Indomaret Run 2026</h1>
    <h2>Privacy Policy – Indomaret Run 2026</h2>
  </header>

  <section>
    <h3>1. Pendahuluan / Introduction</h3>

    <p lang="id">
      <strong>ID:</strong>
      Kebijakan Privasi ini mengatur bagaimana <strong>Indomaret</strong> selaku
      Penyelenggara dan <strong>IRACE</strong> selaku Manajemen Lomba
      (“kami”) mengumpulkan, menggunakan, menyimpan, dan melindungi data pribadi
      peserta (“Anda” atau “IDM Runners”) dalam rangka pelaksanaan
      <strong>Indomaret Run 2026</strong> (“Acara”).
    </p>

    <p lang="en">
      <strong>EN:</strong>
      This Privacy Policy explains how <strong>Indomaret</strong>, as Organizer,
      and <strong>IRACE</strong>, as Race Management (“we”), collect, use, store,
      and protect the personal data of participants (“you” or “IDM Runners”) in
      connection with <strong>Indomaret Run 2026</strong> (“Event”).
    </p>
  </section>

  <section>
    <h3>2. Data yang Dikumpulkan / Data Collected</h3>

    <div lang="id">
      <p><strong>ID:</strong> Kami dapat mengumpulkan data pribadi berikut saat Anda mendaftar:</p>

      <ul>
        <li>Nama lengkap, tanggal lahir, dan jenis kelamin.</li>
        <li>Alamat email, nomor telepon, dan alamat tempat tinggal.</li>
        <li>Informasi identitas (KTP, SIM, atau Paspor) bila diperlukan.</li>
        <li>
          Informasi kesehatan, seperti riwayat medis singkat, alergi, atau kondisi
          khusus.
        </li>
        <li>
          Data pembayaran yang diproses secara aman melalui pihak ketiga dan tidak
          disimpan langsung oleh kami.
        </li>
        <li>Foto atau video yang diambil selama Acara berlangsung.</li>
      </ul>
    </div>

    <div lang="en">
      <p><strong>EN:</strong> We may collect the following personal data upon your registration:</p>

      <ul>
        <li>Full name, date of birth, and gender.</li>
        <li>Email address, phone number, and residential address.</li>
        <li>
          Identity details, such as an ID card, driver’s license, or passport, if
          required.
        </li>
        <li>
          Health information, such as basic medical history, allergies, or special
          conditions.
        </li>
        <li>
          Payment data processed securely through third parties and not stored
          directly by us.
        </li>
        <li>Photos or videos taken during the Event.</li>
      </ul>
    </div>
  </section>

  <section>
    <h3>3. Penggunaan Data / Use of Data</h3>

    <div lang="id">
      <p><strong>ID:</strong> Data Anda akan digunakan untuk:</p>

      <ul>
        <li>Memproses pendaftaran dan mengonfirmasi keikutsertaan peserta.</li>
        <li>Menentukan kategori lomba berdasarkan usia atau kemampuan.</li>
        <li>
          Menyampaikan informasi acara, perubahan jadwal, atau pemberitahuan
          penting.
        </li>
        <li>Menyediakan dukungan medis darurat bila diperlukan.</li>
        <li>Mengelola asuransi peserta, jika berlaku.</li>
        <li>
          Keperluan dokumentasi dan publikasi melalui media sosial, situs web,
          atau materi promosi.
        </li>
        <li>Keperluan analisis dan evaluasi penyelenggaraan Acara.</li>
      </ul>
    </div>

    <div lang="en">
      <p><strong>EN:</strong> Your data will be used to:</p>

      <ul>
        <li>Process registration and confirm participation.</li>
        <li>Determine race category eligibility based on age or ability.</li>
        <li>
          Provide Event information, schedule changes, or important notices.
        </li>
        <li>Enable emergency medical support if needed.</li>
        <li>Manage participant insurance, if applicable.</li>
        <li>
          Support documentation and publication through social media, websites,
          or promotional materials.
        </li>
        <li>Analyze and evaluate the organization of the Event.</li>
      </ul>
    </div>
  </section>

  <section>
    <h3>4. Penyimpanan dan Keamanan Data / Data Storage and Security</h3>

    <div lang="id">
      <p><strong>ID:</strong></p>

      <ul>
        <li>
          Data Anda disimpan dalam sistem yang aman dan hanya dapat diakses oleh
          staf yang berwenang.
        </li>
        <li>
          Kami menerapkan langkah-langkah teknis dan administratif untuk
          melindungi data dari akses, perubahan, atau penyalahgunaan yang tidak
          sah.
        </li>
        <li>
          Data pribadi akan disimpan selama diperlukan untuk tujuan Acara, kecuali
          diwajibkan untuk disimpan lebih lama berdasarkan ketentuan hukum yang
          berlaku.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p><strong>EN:</strong></p>

      <ul>
        <li>
          Your data is stored securely and is accessible only to authorized
          staff.
        </li>
        <li>
          We implement technical and administrative safeguards to protect data
          from unauthorized access, alteration, or misuse.
        </li>
        <li>
          Personal data will be retained only for as long as necessary for the
          purposes of the Event, unless a longer retention period is required by
          applicable law.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>5. Pembagian Data / Data Sharing</h3>

    <div lang="id">
      <p><strong>ID:</strong> Kami dapat membagikan data Anda kepada:</p>

      <ul>
        <li>
          Vendor resmi yang membantu penyelenggaraan Acara, seperti penyedia
          sistem pencatatan waktu lomba, asuransi, dan tim medis.
        </li>
        <li>
          Sponsor untuk tujuan komunikasi atau promosi yang berkaitan dengan
          Acara.
        </li>
        <li>
          Otoritas hukum atau pemerintah apabila diwajibkan oleh peraturan
          perundang-undangan.
        </li>
      </ul>

      <p>Kami tidak akan menjual data pribadi Anda kepada pihak ketiga.</p>
    </div>

    <div lang="en">
      <p><strong>EN:</strong> We may share your data with:</p>

      <ul>
        <li>
          Official vendors supporting the Event, such as race timing providers,
          insurance providers, and medical teams.
        </li>
        <li>Sponsors for communication or Event-related promotional purposes.</li>
        <li>Legal or government authorities if required by applicable law.</li>
      </ul>

      <p>We will not sell your personal data to third parties.</p>
    </div>
  </section>

  <section>
    <h3>6. Hak Peserta / Participant Rights</h3>

    <div lang="id">
      <p><strong>ID:</strong> Anda berhak untuk:</p>

      <ul>
        <li>Mengakses dan meminta salinan data pribadi Anda.</li>
        <li>
          Meminta perbaikan apabila terdapat data yang tidak akurat atau tidak
          lengkap.
        </li>
        <li>
          Menarik persetujuan atas penggunaan data, dengan konsekuensi pembatalan
          pendaftaran apabila persetujuan atas data esensial ditarik.
        </li>
        <li>
          Mengajukan pertanyaan atau keluhan terkait pengelolaan data pribadi.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p><strong>EN:</strong> You have the right to:</p>

      <ul>
        <li>Access and request a copy of your personal data.</li>
        <li>Request the correction of inaccurate or incomplete data.</li>
        <li>
          Withdraw consent for the use of your data, with the consequence that
          your registration may be cancelled if consent for essential data is
          withdrawn.
        </li>
        <li>Submit inquiries or complaints regarding data management.</li>
      </ul>
    </div>
  </section>

  <section>
    <h3>7. Media dan Publikasi / Media and Publication</h3>

    <p lang="id">
      <strong>ID:</strong>
      Dengan berpartisipasi dalam Acara, Anda memberikan izin kepada
      Penyelenggara, IRACE, dan sponsor untuk menggunakan foto, video, serta nama
      Anda dalam publikasi resmi Acara tanpa kompensasi tambahan.
    </p>

    <p lang="en">
      <strong>EN:</strong>
      By participating in the Event, you grant the Organizer, IRACE, and sponsors
      the right to use your photos, videos, and name in official Event
      publications without additional compensation.
    </p>
  </section>

  <section>
    <h3>8. Perubahan Kebijakan / Policy Changes</h3>

    <p lang="id">
      <strong>ID:</strong>
      Kebijakan Privasi ini dapat diperbarui sewaktu-waktu. Setiap perubahan akan
      diumumkan melalui situs web resmi atau kanal media sosial Indomaret Run.
    </p>

    <p lang="en">
      <strong>EN:</strong>
      This Privacy Policy may be updated from time to time. Any changes will be
      announced through the official Indomaret Run website or social media
      channels.
    </p>
  </section>
</article>`; // (keep your long HTML text as-is)
const RULES_TOC_TEXT = `<article class="race-rules">
  <header>
    <h1>
      Indomaret Run 2026 – Aturan Lomba
    </h1>

    <h2>
      Indomaret Run 2026 – Race Rules
    </h2>
  </header>

  <section>
    <h3>
      1. Penyelenggaraan / Event Information
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          <strong>Hari/Tanggal:</strong>
          Minggu, 22 November 2026
        </li>

        <li>
          <strong>Waktu:</strong>
          03:00 WIB – selesai
        </li>

        <li>
          <strong>Venue Start &amp; Finish:</strong>
          Spike Dome, PIK 2
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          <strong>Day/Date:</strong>
          Sunday, 22 November 2026
        </li>

        <li>
          <strong>Time:</strong>
          03:00 AM – until finish
        </li>

        <li>
          <strong>Venue Start &amp; Finish:</strong>
          Spike Dome, PIK 2
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      2. Kategori &amp; Usia Peserta /
      Categories &amp; Age Eligibility
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          <strong>5K:</strong>
          usia minimum 10 tahun pada 22 November 2026.
        </li>

        <li>
          <strong>10K:</strong>
          usia minimum 14 tahun pada 22 November 2026.
        </li>

        <li>
          <strong>21K (Half Marathon):</strong>
          usia minimum 16 tahun pada 22 November 2026.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          <strong>5K:</strong>
          minimum age 10 years as of 22 November 2026.
        </li>

        <li>
          <strong>10K:</strong>
          minimum age 14 years as of 22 November 2026.
        </li>

        <li>
          <strong>21K (Half Marathon):</strong>
          minimum age 16 years as of 22 November 2026.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      3. Kategori Pemenang / Winner Categories
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          <strong>Closed Category:</strong>
          hanya untuk peserta Warga Negara Indonesia (WNI).
        </li>

        <li>
          Pemenang dibagi menjadi:

          <ul>
            <li>
              <strong>General Category (Umum):</strong>
              sesuai batas usia minimum per kategori lomba
              (5K, 10K, dan 21K).
            </li>

            <li>
              <strong>Master Category:</strong>
              peserta dengan usia 45 tahun ke atas pada
              22 November 2026.
            </li>
          </ul>
        </li>

        <li>
          Hadiah General Category (Umum) dapat diberikan
          kepada peserta Warga Negara Indonesia (WNI)
          dengan usia 45 tahun ke atas.
        </li>

        <li>
          Hadiah Master Category hanya diberikan kepada
          WNI berusia 45 tahun ke atas.
        </li>

        <li>
          Penentuan pemenang didasarkan pada
          <em>Gun Time</em>.
        </li>

        <li>
          Setiap peserta hanya berhak memenangkan satu kategori.
          Jika memenuhi syarat di lebih dari satu kategori,
          hadiah dengan nilai tertinggi yang akan diberikan.
        </li>

        <li>
          Penyelenggara berhak melakukan pengujian doping
          terhadap pelari selama kompetisi berlangsung,
          sesuai dengan peraturan yang berlaku.
        </li>

        <li>
          Penyerahan hadiah dan pengesahan gelar juara hanya
          akan dilakukan setelah peserta yang terpilih
          menyelesaikan seluruh prosedur pengujian doping
          dan hasil tes dinyatakan negatif dari zat terlarang.
        </li>

        <li>
          Selama menunggu hasil resmi, pemenang akan berstatus
          sebagai Pemenang Potensial, dan gelar serta hadiah
          belum dapat disahkan atau dialihkan secara final.
        </li>

        <li>
          Apabila hasil pengujian resmi menyatakan bahwa peserta
          terbukti positif menggunakan zat terlarang
          (doping), gelar juara dan hak atas hadiah
          akan dibatalkan.
        </li>

        <li>
          Gelar juara dan hadiah yang dibatalkan tersebut akan
          secara resmi dialihkan kepada peserta berikutnya
          dengan catatan waktu sah dan terverifikasi yang berada
          di bawahnya dalam kategori yang sama.
        </li>

        <li>
          Seluruh peserta dianggap telah menyetujui dan tunduk
          pada kebijakan dan prosedur anti-doping yang
          ditetapkan Penyelenggara.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          <strong>Closed Category:</strong>
          for Indonesian Citizens (WNI) only.
        </li>

        <li>
          Winners will be divided into:

          <ul>
            <li>
              <strong>General Category:</strong>
              open to participants who meet the minimum
              age requirement for each race distance
              (5K, 10K, and 21K).
            </li>

            <li>
              <strong>Master Category:</strong>
              open to participants aged 45 years and above
              as of November 23, 2026.
            </li>
          </ul>
        </li>

        <li>
          Prizes for the General Category may be awarded
          to Indonesian citizens (WNI) aged 45 years
          and above.
        </li>

        <li>
          Prizes for the Master Category are exclusively
          for Indonesian citizens aged 45 years and above.
        </li>

        <li>
          Winners are determined based on
          <em>Gun Time</em>.
        </li>

        <li>
          Each participant is eligible to win only one category.
          If qualified for more than one, the highest-value
          prize will be awarded.
        </li>

        <li>
          The Organizer reserves the right to conduct doping
          tests on runners during the competition,
          in accordance with applicable regulations.
        </li>

        <li>
          Prize presentation and official title confirmation
          will only take place after the selected participants
          have completed all doping test procedures and
          the results are confirmed negative for
          prohibited substances.
        </li>

        <li>
          While awaiting official results, winners will be
          considered Provisional Winners, and titles and prizes
          cannot yet be confirmed or transferred.
        </li>

        <li>
          If official test results confirm that a participant
          has tested positive for doping, the title and
          prize entitlement will be revoked.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      4. Pengambilan Paket Lomba / Race Pack Collection
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          <strong>Hari:</strong>
          20 – 21 November 2026
        </li>

        <li>
          <strong>Waktu:</strong>
          10:00 – 20:00 WIB
        </li>

        <li>
          <strong>Lokasi:</strong>
          Spike Air Dome PIK 2
          (Elevete Dome / Dome C)
        </li>

        <li>
          Peserta harus membawa bukti registrasi
          (konfirmasi email) dan identitas resmi.
        </li>

        <li>
          Pengambilan paket oleh orang lain diperbolehkan
          dengan membawa surat kuasa dan fotokopi
          identitas peserta.
        </li>

        <li>
          Panitia dapat menolak pengambilan apabila dokumen
          persyaratan tidak lengkap.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          <strong>Date:</strong>
          20 – 21 November 2026
        </li>

        <li>
          <strong>Time:</strong>
          10:00 AM – 08:00 PM WIB
        </li>

        <li>
          <strong>Venue:</strong>
          Spike Air Dome PIK 2
          (Elevete Dome / Dome C)
        </li>

        <li>
          Participants must bring registration confirmation
          (email) and a valid ID.
        </li>

        <li>
          Proxy collection is allowed with an authorization
          letter and a copy of the participant’s ID.
        </li>

        <li>
          The committee may reject the collection if the
          required documents are incomplete.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      5. Rundown Lomba / Race Day Schedule
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          <strong>Flag-off 21K:</strong>
          05:00 WIB
        </li>

        <li>
          <strong>Flag-off 10K:</strong>
          05:15 WIB
        </li>

        <li>
          <strong>Flag-off 5K:</strong>
          05:30 WIB
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          <strong>21K Flag-off:</strong>
          05:00 AM
        </li>

        <li>
          <strong>10K Flag-off:</strong>
          05:15 AM
        </li>

        <li>
          <strong>5K Flag-off:</strong>
          05:30 AM
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      6. Cut-Off Time (COT) / Batas Waktu
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          <strong>5K:</strong>
          1 jam 30 menit.
        </li>

        <li>
          <strong>10K:</strong>
          2 jam 30 menit.
        </li>

        <li>
          <strong>21K:</strong>
          3 jam 30 menit.
        </li>

        <li>
          Setiap peserta yang tiba di pos pemeriksaan
          (<em>checkpoint</em>) atau titik tertentu setelah
          melewati batas waktu yang telah ditentukan
          (<em>Cut-Off Time</em> / COT) akan dihentikan
          sementara oleh Marshal di lapangan.
        </li>

        <li>
          Peserta yang dihentikan karena melampaui batas waktu
          tidak berhak memperoleh status resmi Finisher,
          Medali Finisher untuk seluruh kategori, maupun
          Jersey Finisher khusus kategori Half Marathon / 21K.
          Namun, peserta tetap berhak menerima
          <em>refreshment</em> yang disediakan.
        </li>

        <li>
          Peserta yang dihentikan atas keputusan Marshal
          diperbolehkan untuk melanjutkan berlari atau berjalan
          menuju garis Finish, tetapi sepenuhnya menjadi
          tanggung jawab dan risiko pribadi peserta.
          Peserta tetap wajib mematuhi instruksi Marshal,
          terutama yang berkaitan dengan keselamatan dan
          arah lintasan.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          <strong>5K:</strong>
          1 hour 30 minutes.
        </li>

        <li>
          <strong>10K:</strong>
          2 hours 30 minutes.
        </li>

        <li>
          <strong>21K:</strong>
          3 hours 30 minutes.
        </li>

        <li>
          Any participant who arrives at a checkpoint or
          designated point after the Cut-Off Time (COT)
          will be stopped temporarily by the on-field Marshal.
        </li>

        <li>
          Participants who stop due to exceeding the
          Cut-Off Time are not eligible to receive Finisher
          status, Finisher Medal for all categories,
          or Finisher Jersey for the Half Marathon / 21K
          category. However, they will still be entitled
          to receive refreshments provided by the Organizer.
        </li>

        <li>
          Participants who stop by the Marshal’s decision
          may continue running or walking toward the Finish
          Line, but it will be entirely at their own
          responsibility and risk. They must follow all
          Marshal instructions, particularly those concerning
          safety and course direction.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      7. Rute &amp; Pengawasan / Route &amp; Monitoring
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Rute lomba resmi akan diawasi Marshal
          di titik-titik strategis.
        </li>

        <li>
          Peserta wajib melewati semua titik
          <em>checkpoint timing</em>.
        </li>

        <li>
          Peserta dilarang memotong lintasan,
          menggunakan jalan pintas, atau keluar
          dari jalur resmi.
        </li>

        <li>
          Marshal atau petugas keamanan berwenang
          menghentikan peserta yang melakukan pelanggaran.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          The official race route will be monitored
          by Marshals at strategic points.
        </li>

        <li>
          Participants must pass all timing checkpoints.
        </li>

        <li>
          Cutting corners, using shortcuts, or leaving
          the official route is prohibited.
        </li>

        <li>
          Marshals or security officers are authorized
          to stop participants who violate the rules.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      8. Nomor Dada &amp; Chip Waktu /
      Bib &amp; Timing Chip
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Nomor dada harus dipasang di bagian depan jersey
          dan terlihat jelas.
        </li>

        <li>
          Chip waktu, jika digunakan, harus dipasang sesuai
          instruksi panitia.
        </li>

        <li>
          BIB atau chip yang rusak, tertutup, atau salah posisi
          dapat mengakibatkan kegagalan pencatatan waktu
          atau diskualifikasi.
        </li>

        <li>
          Penggandaan BIB atau penggunaan chip palsu
          sangat dilarang.
        </li>

        <li>
          Peserta dilarang memasuki area start dan finish
          jika tidak menggunakan BIB.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          The BIB must be worn on the front of the jersey
          and be clearly visible.
        </li>

        <li>
          The timing chip, if used, must be attached
          as instructed by the committee.
        </li>

        <li>
          Damaged, obscured, or improperly placed BIB or chip
          may lead to timing failure or disqualification.
        </li>

        <li>
          Duplication of the BIB or chip is strictly prohibited.
        </li>

        <li>
          Participants are prohibited from entering
          the start and finish areas without a BIB.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      9. Pos Air Minum &amp; Refreshment /
      Water Station &amp; Refreshment
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Pos air minum (<em>water stations</em>) tersedia
          di beberapa titik sepanjang rute untuk
          semua kategori.
        </li>

        <li>
          <em>Refreshment</em> berupa minuman dan makanan
          ringan tersedia di area finish.
        </li>

        <li>
          Peserta dapat mengambil dari pos tersebut sepanjang
          tidak mengganggu pelari lain.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          Water stations will be available at several points
          along the route for all categories.
        </li>

        <li>
          Refreshments, including drinks and light snacks,
          are available at the finish area.
        </li>

        <li>
          Participants may use these stations without
          impeding other runners.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      10. Layanan Medis &amp; Keamanan /
      Medical &amp; Safety Services
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Pos medis, tim medis, dan ambulans tersedia
          di titik strategis di rute dan area finish.
        </li>

        <li>
          Peserta wajib mencantumkan kondisi kesehatan
          atau kondisi medis penting saat pendaftaran.
        </li>

        <li>
          Tim medis memiliki hak untuk menghentikan peserta
          apabila kondisi medis memburuk atau membahayakan.
        </li>

        <li>
          Biaya medis lanjutan di rumah sakit rujukan
          menjadi tanggung jawab peserta.
        </li>

        <li>
          Peserta harus mematuhi instruksi medis dan
          evakuasi apabila diperlukan.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          Medical posts, medical teams, and ambulances
          will be located at strategic points along
          the route and at the finish area.
        </li>

        <li>
          Participants must declare health conditions
          or significant medical issues during registration.
        </li>

        <li>
          The medical team has the right to stop participants
          if their condition worsens or becomes unsafe.
        </li>

        <li>
          Additional medical costs at referral hospitals
          are the participants’ responsibility.
        </li>

        <li>
          Participants must adhere to medical directives
          and evacuations if necessary.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      11. Penitipan Barang / Bag Deposit
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Fasilitas penitipan barang disediakan
          di lokasi acara.
        </li>

        <li>
          Fasilitas bag deposit akan dibuka mulai
          pukul 03:00 WIB sampai pukul 10:00 WIB.
        </li>

        <li>
          Barang berharga seperti HP, dompet,
          dokumen berharga, dan perhiasan
          tidak dianjurkan untuk dititipkan.
        </li>

        <li>
          Peserta harus menaruh barang miliknya dalam
          satu tas sebelum diserahkan.
        </li>

        <li>
          Pengambilan barang harus dilakukan secara
          keseluruhan dan tidak dapat dilakukan
          secara terpisah.
        </li>

        <li>
          Panitia tidak bertanggung jawab atas kehilangan
          atau kerusakan barang titipan.
        </li>

        <li>
          Penyelenggara dapat mengubah jam layanan penitipan
          tanpa pemberitahuan.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          A bag deposit facility will be available
          at the Event venue.
        </li>

        <li>
          Bag deposit service operates from
        03:00 WIB to 10:00 WIB.
        </li>

        <li>
          Valuables such as mobile phones, wallets,
          important documents, and jewelry are not
          recommended to be deposited.
        </li>

        <li>
          Participants must place all their belongings
          in one bag before handing it over.
        </li>

        <li>
          Bag collection must be done all at once,
          with no partial retrieval.
        </li>

        <li>
          The Organizer is not responsible for any loss
          or damage to deposited items.
        </li>

        <li>
          The Organizer reserves the right to adjust
          the operating hours of the bag deposit service
          without prior notice.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      12. Prosedur Start / Start Procedure
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Setiap kategori jarak Indomaret Run 2026
          akan menggunakan sistem
          <em>single mass start</em>, di mana semua
          peserta pada setiap kategori akan mulai
          secara bersamaan dalam satu waktu.
        </li>

        <li>
          Waktu start dan cut-off start diatur
          dalam tabel berikut:
        </li>
      </ul>

      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th scope="col">
                Kategori
              </th>

              <th scope="col">
                Start
              </th>

              <th scope="col">
                Cut-Off Start
              </th>

              <th scope="col">
                Aturan Kehadiran
              </th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>
                Half Marathon (21K)
              </td>

              <td>
                05:00 WIB
              </td>

              <td>
                05:07 WIB
              </td>

              <td>
                Peserta yang tiba atau masuk gate setelah
                pukul 05:07 WIB tidak dapat bergabung pada
                start 21K dan akan diarahkan untuk mengikuti
                start kategori berikutnya, jika tersedia
                dan memungkinkan oleh tim Race Operations.
              </td>
            </tr>

            <tr>
              <td>
                10K
              </td>

              <td>
                05:15 WIB
              </td>

              <td>
                05:22 WIB
              </td>

              <td>
                Peserta yang tiba setelah pukul 05:22 WIB
                tidak dapat bergabung pada start 10K dan
                akan dialihkan ke start kategori berikutnya
                sesuai arahan petugas.
              </td>
            </tr>

            <tr>
              <td>
                5K
              </td>

              <td>
                05:30 WIB
              </td>

              <td>
                05:37 WIB
              </td>

              <td>
                Setelah pukul 05:37 WIB, gate start akan
                dialihkan menjadi gate finish, sehingga
                peserta yang datang terlambat tidak dapat
                mengikuti kategori 5K.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        Untuk alasan keamanan lintasan dan keselamatan,
        peserta dilarang menggunakan:
      </p>

      <ul>
        <li>
          Stroller atau kereta bayi.
        </li>

        <li>
          Kursi roda untuk kategori non-para.
        </li>

        <li>
          Inline skate, skateboard, hoverboard,
          atau perangkat roda lainnya.
        </li>

        <li>
          Benda yang mengganggu ruang bergerak peserta lain,
          seperti tiang bendera besar, balon besar,
          atau kostum yang menghalangi jalur.
        </li>

        <li>
          Peserta yang membawa atau menggunakan benda-benda
          tersebut tidak diperkenankan memasuki area start.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <p>
        Each race category at Indomaret Run 2026
        will use a single mass start system, where all
        participants in each category will begin together
        at the same time.
      </p>

      <div class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th scope="col">
                Category
              </th>

              <th scope="col">
                Start
              </th>

              <th scope="col">
                Cut-Off Start
              </th>

              <th scope="col">
                Attendance Rules
              </th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td>
                Half Marathon (21K)
              </td>

              <td>
                05:00 WIB
              </td>

              <td>
                05:07 WIB
              </td>

              <td>
                Participants who arrive or enter the gate
                after 05:07 WIB will not be allowed to join
                the 21K start. They will be directed to join
                the next available category if possible
                and approved by the Race Operations team.
              </td>
            </tr>

            <tr>
              <td>
                10K
              </td>

              <td>
                05:15 WIB
              </td>

              <td>
                05:22 WIB
              </td>

              <td>
                Participants arriving after 05:22 WIB
                cannot join the 10K start and will be
                redirected to the next available category
                according to the Marshals’ instructions.
              </td>
            </tr>

            <tr>
              <td>
                5K
              </td>

              <td>
                05:30 WIB
              </td>

              <td>
                05:37 WIB
              </td>

              <td>
                After 05:37 WIB, the start gate will be
                switched to the finish gate. Participants
                arriving late will not be able to join
                the 5K category.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        For safety and smooth movement on the course,
        participants are not allowed to bring or use:
      </p>

      <ul>
        <li>
          Strollers or baby carriages.
        </li>

        <li>
          Wheelchairs for non-para categories.
        </li>

        <li>
          Inline skates, skateboards, hoverboards,
          or any similar wheeled devices.
        </li>

        <li>
          Items that may obstruct others, such as large flags,
          big balloons, or oversized costumes that
          block the route.
        </li>

        <li>
          Participants carrying or using these items
          will not be permitted to enter the start area.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      13. Diskualifikasi / Disqualification
    </h3>

    <div lang="id">
      <p>
        <strong>
          ID: Peserta akan didiskualifikasi jika:
        </strong>
      </p>

      <ul>
        <li>
          Menggunakan BIB atau chip palsu,
          atau milik orang lain.
        </li>

        <li>
          Memotong rute atau keluar dari jalur resmi.
        </li>

        <li>
          Menggunakan kendaraan, sepeda,
          atau alat bantu ilegal.
        </li>

        <li>
          Bertindak tidak sportif atau menghalangi
          peserta lain.
        </li>

        <li>
          Melanggar instruksi Marshal atau petugas lomba.
        </li>

        <li>
          Menolak verifikasi identitas ketika diminta.
        </li>

        <li>
          Menggunakan earphone dengan fitur yang mengganggu
          konsentrasi, seperti
          <em>noise cancelling</em>.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>
          EN: Participants will be disqualified if they:
        </strong>
      </p>

      <ul>
        <li>
          Use a fake or someone else’s BIB or chip.
        </li>

        <li>
          Cut the course or exit the official route.
        </li>

        <li>
          Use vehicles, bicycles, or unauthorized aids.
        </li>

        <li>
          Show unsportsmanlike conduct or obstruct others.
        </li>

        <li>
          Disobey the Marshal or race official instructions.
        </li>

        <li>
          Refuse identity verification when requested.
        </li>

        <li>
          Use earphones with features that disrupt
          concentration, such as noise cancelling.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      14. Keberatan, Sengketa &amp; Klaim /
      Protests, Disputes &amp; Claims
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Keberatan atas hasil lomba dapat diajukan maksimal
          30 menit setelah hasil diumumkan secara resmi.
        </li>

        <li>
          Keberatan yang diajukan melewati batas waktu
          tidak akan diproses.
        </li>

        <li>
          Keputusan panitia bersifat final dan tidak dapat
          diganggu gugat.
        </li>

        <li>
          Peserta dapat menyerahkan bukti pendukung,
          seperti foto, data GPS, dan bukti lainnya.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          Protests against race results must be submitted
          within 30 minutes after the official
          result announcement.
        </li>

        <li>
          Protests submitted after the deadline
          will not be processed.
        </li>

        <li>
          The committee’s decision is final and binding.
        </li>

        <li>
          Participants may submit supporting evidence,
          such as photos, GPS data, and other evidence.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      15. Hadiah, Verifikasi &amp; Pajak /
      Prizes, Verification &amp; Taxes
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Calon pemenang wajib menunjukkan identitas asli
          dalam bentuk fisik berupa KTP, KIA,
          atau Kartu Keluarga (KK) pada saat
          proses verifikasi.
        </li>

        <li>
          Fotokopi, foto digital, maupun identitas digital
          tidak akan diterima sebagai bukti identitas
          yang sah.
        </li>

        <li>
          Setiap Calon Pemenang Potensial wajib menunjukkan
          Nomor Pokok Wajib Pajak (NPWP) yang masih berlaku
          kepada panitia saat proses verifikasi
          di Tenda Verifikasi Pemenang.
        </li>

        <li>
          Peserta di bawah umur yang berstatus
          Calon Pemenang Potensial diizinkan menggunakan
          NPWP salah satu orang tua atau wali untuk
          tujuan administrasi pemenang.
        </li>

        <li>
          Jika verifikasi gagal atau data tidak sesuai,
          status pemenang akan dibatalkan.
        </li>

        <li>
          Setiap peserta hanya berhak atas satu
          kategori pemenang.
        </li>

        <li>
          Pajak hadiah akan ditanggung oleh Indomaret.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          All potential winners are required to present
          their original physical identification,
          such as National ID Card (KTP),
          Child ID Card (KIA), or Family Card (KK),
          during the verification process.
        </li>

        <li>
          Photocopies, digital photos, or digital
          identification will not be accepted
          as valid proof of identity.
        </li>

        <li>
          Each Potential Winner must present their
          Tax Identification Number (NPWP) to the Organizer
          during the verification process at the
          Winner Verification Tent.
        </li>

        <li>
          Minor participants designated as Potential Winners
          are permitted to use the NPWP of one of their
          parents or guardians for administration purposes.
        </li>

        <li>
          If verification fails or data mismatches,
          the winning status will be revoked.
        </li>

        <li>
          Each participant is only entitled to
          one winner category.
        </li>

        <li>
          Prize taxes will be borne by Indomaret.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      16. Dokumentasi &amp; Hak Citra /
      Documentation &amp; Image Rights
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Dengan mendaftar, peserta menyetujui bahwa panitia
          (Indomaret dan IRACE) serta mitra dapat menggunakan
          foto, video, nama, dan hasil lomba untuk dokumentasi,
          promosi, serta publikasi tanpa kompensasi tambahan.
        </li>

        <li>
          Peserta dilarang membawa fotografer pribadi
          tanpa izin tertulis dari panitia.
        </li>

        <li>
          Materi foto atau video resmi menjadi hak milik
          panitia dan dapat digunakan tanpa batas waktu.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          By registering, participants agree that
          the committee (Indomaret and IRACE) and partners
          may use photos, videos, names, and race results
          for documentation, promotion, and publication
          without additional compensation.
        </li>

        <li>
          Participants are prohibited from bringing
          private photographers without written permission
          from the committee.
        </li>

        <li>
          Official photo or video materials are the property
          of the committee and may be used indefinitely.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      17. Perubahan Acara / Event Changes
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Panitia berhak mengubah jadwal, rute, aturan lomba,
          lokasi, atau aspek lainnya demi keamanan
          atau karena kendala logistik.
        </li>

        <li>
          Semua perubahan akan diumumkan melalui situs resmi
          dan media sosial Indomaret Run.
        </li>

        <li>
          Peserta dianggap menyetujui perubahan
          setelah diumumkan.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          The committee reserves the right to modify
          the schedule, route, race rules, venue,
          or other aspects for safety or logistical reasons.
        </li>

        <li>
          All changes will be announced through the official
          Indomaret Run website and social media.
        </li>

        <li>
          Participants are deemed to agree to changes
          once announced.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      18. Tanggung Jawab &amp; Pembebasan /
      Liability &amp; Release
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Peserta menyadari bahwa lomba jalan raya mengandung
          risiko, termasuk cedera, kelelahan,
          dan kecelakaan.
        </li>

        <li>
          Peserta membebaskan Indomaret, IRACE, sponsor,
          panitia, Marshal, dan pihak terkait dari semua
          tuntutan atas cedera, kerugian, atau kematian,
          kecuali dalam kasus kelalaian yang terbukti
          dilakukan oleh panitia.
        </li>

        <li>
          Jika panitia terbukti bertanggung jawab,
          kompensasi maksimal tidak lebih dari
          biaya pendaftaran.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          Participants acknowledge that road racing
          carries risks, including injury, fatigue,
          and accidents.
        </li>

        <li>
          Participants release Indomaret, IRACE, sponsors,
          the committee, Marshals, and related parties
          from all claims for injury, loss, or death,
          except in cases of proven negligence
          by the committee.
        </li>

        <li>
          If the committee is found liable,
          maximum compensation shall not exceed
          the registration fee paid.
        </li>
      </ul>
    </div>
  </section>

  <section>
    <h3>
      19. Penambahan atau Perubahan Aturan /
      Amendments &amp; Addenda
    </h3>

    <div lang="id">
      <p>
        <strong>ID (Bahasa Indonesia):</strong>
      </p>

      <ul>
        <li>
          Panitia dapat menambah, mengubah,
          atau meniadakan aturan kapan saja.
        </li>

        <li>
          Semua perubahan akan diumumkan melalui situs resmi
          dan media sosial.
        </li>

        <li>
          Peserta dianggap menyetujui perubahan
          setelah diumumkan.
        </li>
      </ul>
    </div>

    <div lang="en">
      <p>
        <strong>EN (English):</strong>
      </p>

      <ul>
        <li>
          The committee may add, change,
          or waive rules at any time.
        </li>

        <li>
          All changes will be announced through
          the official website and social media.
        </li>

        <li>
          Participants are deemed to agree to changes
          once announced.
        </li>
      </ul>
    </div>
  </section>
</article>`; // (keep your long HTML text as-is)

// helpers
const pad2 = (n) => String(n ?? 0).padStart(2, '0');
const fmtTime = (t) =>
  t && typeof t === 'object'
    ? `${pad2(t.hour)}:${pad2(t.minute)}:${pad2(t.second)}`
    : t || '—';

const show = (v) => {
  if (v == null || v === '') return '—';
  if (typeof v === 'object') {
    if ('label' in v) return v.label ?? v.value ?? '—';
    if ('value' in v) return v.value ?? '—';
  }
  return String(v);
};

const normalizeRace = (race) => {
  const s = String(race || '').toLowerCase();
  if (s.includes('21')) return '21k';
  if (s.includes('10')) return '10k';
  if (s.includes('5')) return '5k';
  return s || '10k';
};

const showSizeValue = (size) => {
  if (!size) return '—';
  if (typeof size === 'object') return size.value || size.label || '—';
  return String(size).split(' - ')[0];
};

function pad(num) {
  return String(num).padStart(2, '0');
}

function normalizeRegistration(input) {
  const output = {};

  for (const [key, value] of Object.entries(input)) {
    if (key === 'org_kom') continue;

    if (
      key === 'gender' &&
      value &&
      typeof value === 'object' &&
      'value' in value
    ) {
      output[key] = value.value ?? '-';
      continue;
    }

    if (key === 'org_com') {
      output[key] = input.org_kom === 'yes' ? (value ?? '-') : '-';
      continue;
    }

    if (value instanceof File || value instanceof Blob) {
      output[key] = value;
      continue;
    }

    if (value == null) {
      output[key] = '-';
      continue;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      if (
        ['size', 'belt_size', 'finisher_size'].includes(key) &&
        'value' in value
      ) {
        output[key] = value.value ?? '-';
      } else if ('hour' in value && 'minute' in value && 'second' in value) {
        output[key] = `${pad(value.hour)}:${pad(value.minute)}:${pad(
          value.second,
        )}`;
      } else if ('label' in value) {
        output[key] = value.label ?? '-';
      } else {
        output[key] = '-';
      }
      continue;
    }

    output[key] = value;
  }

  return output;
}

function Section({ title, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: `${RADIUS_PX}px`,
        borderColor: SECTION_BORDER,
        borderWidth: 2,
        p: { xs: 2, md: 3 },
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 800,
          mb: 1.5,
          fontSize: 'clamp(1.25rem, 0.55vw + 1rem, 1.5rem)',
        }}
      >
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

function KV({ label, value }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function TocDialog({ open, onClose, title, text }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography
          variant="body2"
          dangerouslySetInnerHTML={{
            __html: text,
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const postRequest = (type, data, options = {}) => {
  if (parseInt(type) === 231) {
    return api.post('/indomaret/event/register', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
      baseURL: `${process.env.REACT_APP_V1_API_URL}/api`,
    });
  } else {
    return api.post('/indomaret/event/register', data);
  }
};

export default function SummaryIndomaretRegistration() {
  const navigate = useNavigate();
  const { state, pathname } = useLocation();

  const [registeraLoading, setRegisteraLoading] = useState(false);
  const [tocModal, setTocModal] = useState({
    open: false,
    title: '',
    text: '',
  });
  const [resultDialog, setResultDialog] = useState({
    open: false,
    status: 'success',
    title: '',
    message: '',
  });
  const openToc = (title, text) => setTocModal({ open: true, title, text });
  const closeToc = () => setTocModal((p) => ({ ...p, open: false }));

  // Data dari form
  const values = state?.values || null;
  const meta = state?.meta || null;

  // path balik form
  const registrationPath =
    pathname && /\/summary\/?$/.test(pathname)
      ? pathname.replace(/\/summary\/?$/, '')
      : '/event/register/indomaret';

  // Normalisasi untuk rule UI
  const catEnum = normalizeRace(values?.race_category);
  const orgKom = String(values?.org_kom || '').toLowerCase();
  const is21kCompetitive =
    catEnum === '21k' &&
    (values?.rpt_21?.value || values?.rpt_21) === 'competitive';

  // === RHF untuk Agreement + (opsional) Proof of Run ===
  const {
    control,
    handleSubmit,
    watch,
    setError, // <-- NEW
    clearErrors, // <-- NEW
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      proof_file: null, // File
      agree_terms: false,
      agree_waiver: false,
      agree_privacy: false,
      agree_rules: false,
      drop_bag: '',
    },
  });

  // file validator (shared)
  const validateProof = (f) => {
    const MAX = 2 * 1024 * 1024; // 2MB
    if (!f) return 'Please upload your proof of run (image).';
    if (!(f instanceof File)) return 'Invalid file.';
    if (f.size > MAX) return 'Max file size is 2MB.';
    if (!/^image\//.test(f.type)) return 'File must be an image.';
    if (f.size === 0) return 'File is empty or unreadable.';
    return true;
  };

  const proofFile = watch('proof_file');
  const previewUrl = useMemo(
    () => (proofFile ? URL.createObjectURL(proofFile) : null),
    [proofFile],
  );
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Rows (compact)
  const rows = useMemo(() => {
    const arr = [
      { label: 'Email', val: values?.email },
      { label: 'Race Category', val: show(values?.race_category) },
      { label: 'Name', val: values?.name },
      { label: 'Phone Number', val: values?.contact },
      { label: 'Gender', val: show(values?.gender) },
      { label: 'Nationality', val: show(values?.nationality) },
      { label: 'Address', val: show(values?.address) },
      { label: 'Province', val: show(values?.province) },
      {
        label: 'City / Regency',
        val: show(values?.city_regency),
      },
      { label: 'ID Type', val: show(values?.id_type) },
      { label: 'ID Number', val: values?.id_number },
      { label: 'Birth Date', val: values?.brith_date },
      { label: 'Blood Type', val: show(values?.blood_type) },
      ...(orgKom === 'yes' && String(values?.org_com || '').trim()
        ? [{ label: 'Club Name', val: values?.org_com }]
        : []),
      ...(catEnum === '21k' && values?.rpt_21
        ? [{ label: 'Race Prediction Time', val: show(values?.rpt_21) }]
        : []),
      ...(catEnum === '10k' && values?.rpt_10
        ? [{ label: 'Race Prediction Time', val: show(values?.rpt_10) }]
        : []),
      { label: 'Expected Finish Time', val: fmtTime(values?.exp_fin_time) },
      { label: 'Medical Info', val: values?.medical_note },
      { label: 'Jersey Size', val: showSizeValue(values?.size) },
      { label: 'Belt Size', val: show(values?.belt_size) },
      ...(catEnum === '21k' && values?.finisher_size
        ? [
            {
              label: 'Finisher Jacket Size',
              val: show(values?.finisher_size),
            },
          ]
        : []),
      { label: 'Emergency Name', val: values?.emergency_person },
      { label: 'Emergency Contact', val: values?.emergency_contact },
      { label: 'Emergency Relation', val: show(values?.emergency_relation) },
    ];
    return arr;
  }, [values, orgKom, catEnum]);

  // Grid
  const RowGrid = ({ rows }) => (
    <Grid container spacing={1.25}>
      {rows.map((r, i) => (
        <Grid item key={`${r.label}-${i}`} xs={12} md={r.span === 12 ? 12 : 6}>
          <KV label={r.label} value={r.val ?? '—'} />
        </Grid>
      ))}
    </Grid>
  );

  const onEdit = () => {
    navigate(registrationPath, {
      replace: true,
      state: { initialValues: values, meta },
    });
  };

  // SUBMIT: original
  const onSubmit = async (agreements) => {
    const normalizedValues = normalizeRegistration(values);
    const payloadObject = {
      runner: normalizedValues,
      meta,
      agreements: {
        agree_terms: !!agreements.agree_terms,
        agree_waiver: !!agreements.agree_waiver,
        agree_privacy: !!agreements.agree_privacy,
        agree_rules: !!agreements.agree_rules,
        drop_bag_use: agreements.drop_bag === 'use',
      },
    };

    // --- real submit (unchanged) ---
    const fd = new FormData();
    fd.append('payload', JSON.stringify(payloadObject));
    if (is21kCompetitive && agreements.proof_file instanceof File) {
      fd.append(
        'proof_file',
        agreements.proof_file,
        agreements.proof_file.name,
      );
    }

    try {
      setRegisteraLoading(true);

      await postRequest(meta?.category_id, fd);

      setResultDialog({
        open: true,
        status: 'success',
        title: 'Registration Successful',
        message:
          'Your registration was successful.\nE-ticket will be sent via email.',
      });
    } catch (error) {
      const rawMessage =
        error?.response?.data?.message ||
        error?.response?.data ||
        error?.message ||
        'Submit failed';

      const message =
        typeof rawMessage === 'string'
          ? rawMessage
          : JSON.stringify(rawMessage);

      setResultDialog({
        open: true,
        status: 'error',
        title: 'Registration Failed',
        message,
      });
    } finally {
      setRegisteraLoading(false);
    }
  };

  // PRE-SUBMIT GUARD: ensure proof_file valid & show error near filename + alert
  const onPreSubmit = async (agreements) => {
    if (is21kCompetitive) {
      const verdict = validateProof(agreements.proof_file);
      if (verdict !== true) {
        setError('proof_file', { type: 'manual', message: verdict });
        alert(`Invalid file: ${verdict}`);
        document
          .getElementById('proof-input')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    await onSubmit(agreements);
  };

  const closeResultDialog = () => {
    const currentStatus = resultDialog.status;

    setResultDialog((prev) => ({
      ...prev,
      open: false,
    }));

    if (currentStatus === 'success') {
      navigate(HOME_PATH, {
        replace: true,
      });
    }
  };

  if (registeraLoading)
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: { xs: 8, sm: 5 },
        }}
      >
        <CircularProgress size="4rem" />
      </Box>
    );

  if (!values) {
    return (
      <Box
        sx={{
          maxWidth: '56rem',
          mx: 'auto',
          px: { xs: 2, md: 0 },
          py: 6,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 900,
            mb: 1,
            fontSize: 'clamp(1.625rem, 1.1vw + 1.2rem, 2.25rem)',
          }}
        >
          No data to summarize
        </Typography>
        <Typography sx={{ color: 'text.secondary', mb: 3 }}>
          Please fill the registration form first.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate(registrationPath, { replace: true })}
        >
          Back to Form
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onPreSubmit)} // <-- use guard
      noValidate
      sx={{
        width: '100%',
        maxWidth: '56rem',
        mx: 'auto',
        px: { xs: 2, md: 0 },
        py: { xs: 5, sm: 3, md: 0 },
        mt: { xs: 3, md: 5, xl: 8 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          mb: 2,
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 900,
            fontSize: 'clamp(1.625rem, 1.1vw + 1.2rem, 2.25rem)',
          }}
        >
          Registration Summary
        </Typography>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: 'clamp(1rem, 0.35vw + 0.9rem, 1.25rem)',
          }}
        >
          {show(values.race_category)}
        </Typography>
      </Box>

      <Stack spacing={2.5}>
        <Section title="Registration Details">
          <RowGrid rows={rows} />

          {/* === Proof of Run (only 21K - competitive) === */}
          {is21kCompetitive && (
            <Box sx={{ mt: 2.5 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  fontSize: 'clamp(1.125rem, 0.5vw + 1rem, 1.375rem)',
                }}
              >
                Proof of Run (required for 21K – Competitive)
              </Typography>

              <Controller
                name="proof_file"
                control={control}
                rules={{ validate: (f) => validateProof(f) }}
                render={({ field: { value, onChange } }) => (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'stretch',
                      flexWrap: 'wrap',
                      border: '1px solid #E5E7EB',
                      borderRadius: 2,
                      p: 1.5,
                    }}
                  >
                    {/* Left: Choose file + filename + error right below */}
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        flex: '1 1 280px',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <input
                        id="proof-input"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files?.[0] || null;
                          if (file) {
                            const verdict = validateProof(file);
                            if (verdict !== true) {
                              setError('proof_file', {
                                type: 'manual',
                                message: verdict,
                              });
                              alert(`Invalid file: ${verdict}`);
                              onChange(null); // don't store invalid file
                              return;
                            }
                            clearErrors('proof_file');
                          }
                          onChange(file);
                        }}
                      />
                      <label htmlFor="proof-input">
                        <Button variant="outlined" component="span">
                          Choose Image
                        </Button>
                      </label>

                      {/* filename */}
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        {value
                          ? `${value.name} (${Math.ceil(
                              (value.size || 0) / 1024,
                            )} KB)`
                          : 'No file chosen'}
                      </Typography>

                      {/* error directly under filename */}
                      {errors.proof_file ? (
                        <FormHelperText error sx={{ m: 0 }}>
                          {errors.proof_file.message}
                        </FormHelperText>
                      ) : (
                        <FormHelperText sx={{ m: 0 }}>
                          Max file size: 2MB. Images only.
                        </FormHelperText>
                      )}
                    </Box>

                    {/* Right: Preview */}
                    <Box
                      sx={{
                        flex: '1 1 240px',
                        minHeight: 160,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        borderRadius: 2,
                        bgcolor: '#FAFAFA',
                      }}
                    >
                      {previewUrl ? (
                        <img
                          src={previewUrl}
                          alt="Proof preview"
                          style={{
                            maxWidth: '100%',
                            maxHeight: 240,
                            objectFit: 'contain',
                            display: 'block',
                          }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          No preview
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              />
            </Box>
          )}

          {/* Divider antara detail dan agreement */}
          <Divider sx={{ my: 2.5 }} />
          {/* === Drop Bag? (sebelum Agreements) === */}
          <Box sx={{ mt: 2.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontSize: 'clamp(1.125rem, 0.5vw + 1rem, 1.375rem)',
              }}
            >
              Drop Bag?
            </Typography>
            <Controller
              name="drop_bag"
              control={control}
              rules={{ required: 'Please select one option.' }}
              render={({ field }) => (
                <>
                  <RadioGroup
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  >
                    <FormControlLabel
                      value="use"
                      control={<Radio />}
                      label={
                        <Typography variant="body2">
                          I will use the <strong>Drop Bag Facility</strong> on
                          Race Day and comply to the{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              openToc(
                                'Drop Bag Facility — Terms & Conditions',
                                DROP_BAG_TOC_TEXT,
                              )
                            }
                          >
                            terms and conditions
                          </Link>
                          .
                        </Typography>
                      }
                      sx={{
                        m: 0.25,
                        px: { xs: 1, md: 1.25 },
                        py: { xs: 0.5, md: 0.75 },
                        border: '1px solid #E5E7EB',
                        borderRadius: 2,
                      }}
                    />
                    <FormControlLabel
                      value="not_use"
                      control={<Radio />}
                      label={
                        <Typography variant="body2">
                          I will not use the Drop Bag Facility
                        </Typography>
                      }
                      sx={{
                        m: 0.25,
                        px: { xs: 1, md: 1.25 },
                        py: { xs: 0.5, md: 0.75 },
                        border: '1px solid #E5E7EB',
                        borderRadius: 2,
                      }}
                    />
                  </RadioGroup>
                  {errors.drop_bag && (
                    <FormHelperText error sx={{ ml: 0, mt: 0.5 }}>
                      {errors.drop_bag.message}
                    </FormHelperText>
                  )}
                </>
              )}
            />
          </Box>

          {/* Divider antara detail dan agreement */}
          <Divider sx={{ my: 2.5 }} />

          {/* === Agreements === */}
          <Box sx={{ mt: 0.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 1,
                fontSize: 'clamp(1.125rem, 0.5vw + 1rem, 1.375rem)',
              }}
            >
              Agreements
            </Typography>

            <FormGroup sx={{ gap: 0.5 }}>
              <Controller
                name="agree_terms"
                control={control}
                rules={{ validate: (v) => v || 'You must accept the Terms.' }}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={<Checkbox {...field} checked={!!field.value} />}
                      label={
                        <Typography variant="body2">
                          I have read and agree to the{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              openToc('Terms & Conditions', TERMS_TOC_TEXT)
                            }
                          >
                            Terms & Conditions
                          </Link>
                        </Typography>
                      }
                    />
                    {errors.agree_terms && (
                      <FormHelperText error sx={{ ml: 4 }}>
                        {errors.agree_terms.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />

              <Controller
                name="agree_waiver"
                control={control}
                rules={{ validate: (v) => v || 'You must accept the waiver.' }}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={<Checkbox {...field} checked={!!field.value} />}
                      label={
                        <Typography variant="body2">
                          I acknowledge the{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              openToc('Event Waiver', WAIVER_TOC_TEXT)
                            }
                          >
                            Event Waiver
                          </Link>
                        </Typography>
                      }
                    />
                    {errors.agree_waiver && (
                      <FormHelperText error sx={{ ml: 4 }}>
                        {errors.agree_waiver.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />

              <Controller
                name="agree_privacy"
                control={control}
                rules={{
                  validate: (v) => v || 'You must accept the Privacy Policy.',
                }}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={<Checkbox {...field} checked={!!field.value} />}
                      label={
                        <Typography variant="body2">
                          I agree to the{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              openToc('Privacy Policy', PRIVACY_TOC_TEXT)
                            }
                          >
                            Privacy Policy
                          </Link>
                        </Typography>
                      }
                    />
                    {errors.agree_privacy && (
                      <FormHelperText error sx={{ ml: 4 }}>
                        {errors.agree_privacy.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />

              <Controller
                name="agree_rules"
                control={control}
                rules={{
                  validate: (v) => v || 'You must acknowledge the race rules.',
                }}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      control={<Checkbox {...field} checked={!!field.value} />}
                      label={
                        <Typography variant="body2">
                          I agree to follow the{' '}
                          <Link
                            component="button"
                            type="button"
                            underline="hover"
                            onClick={() =>
                              openToc('Race Rules', RULES_TOC_TEXT)
                            }
                          >
                            Race Rules
                          </Link>
                        </Typography>
                      }
                    />
                    {errors.agree_rules && (
                      <FormHelperText error sx={{ ml: 4 }}>
                        {errors.agree_rules.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </FormGroup>
          </Box>
        </Section>
      </Stack>

      <Box
        sx={{ mt: 2.5, display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}
      >
        <Button type="button" variant="outlined" onClick={onEdit}>
          Edit
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isSubmitting || registeraLoading}
        >
          Confirm
        </Button>
      </Box>

      {/* Reusable ToC dialog */}
      <TocDialog
        open={tocModal.open}
        onClose={() => setTocModal((p) => ({ ...p, open: false }))}
        title={tocModal.title}
        text={tocModal.text}
      />
      <RegStatusDialog
        open={resultDialog.open}
        status={resultDialog.status}
        title={resultDialog.title}
        message={resultDialog.message}
        buttonLabel={
          resultDialog.status === 'success' ? 'Back to Event' : 'Close'
        }
        onClose={closeResultDialog}
      />
    </Box>
  );
}
